// Size maths for Resize (FR-21): pixels with aspect lock, percent, cm/inch at a DPI,
// presets and "fill" cropping. Pure functions, no DOM.

export type Size = { width: number; height: number };
export type Rect = { x: number; y: number; width: number; height: number };
export type Unit = "cm" | "in";

/** Largest canvas iOS Safari can draw (4096 × 4096 = 16.7 megapixels). */
export const MAX_PIXELS = 16_777_216;
export const MAX_SIDE = 16_384;

export const CM_PER_INCH = 2.54;

export function toPixels(value: number, unit: Unit, dpi: number): number {
  const inches = unit === "cm" ? value / CM_PER_INCH : value;
  return Math.round(inches * dpi);
}

export function fromPixels(px: number, unit: Unit, dpi: number): number {
  const inches = px / dpi;
  return unit === "cm" ? inches * CM_PER_INCH : inches;
}

/** With the aspect lock on: the other side for a new width or height. */
export function linkedSide(
  source: Size,
  changed: "width" | "height",
  value: number,
): number {
  const ratio =
    changed === "width"
      ? source.height / source.width
      : source.width / source.height;
  return Math.max(1, Math.round(value * ratio));
}

export function scaleByPercent(source: Size, percent: number): Size {
  return {
    width: Math.max(1, Math.round((source.width * percent) / 100)),
    height: Math.max(1, Math.round((source.height * percent) / 100)),
  };
}

/** The new size for one image of a batch.
 *  - "exact": every image gets width × height (see `fit` for shape changes)
 *  - "width"/"height": that side is fixed and the other follows the image's
 *    own shape, so a batch of mixed photos is never squashed. */
export function sizeFor(
  source: Size,
  target: Size,
  keep: "exact" | "width" | "height",
): Size {
  if (keep === "width") {
    return {
      width: target.width,
      height: linkedSide(source, "width", target.width),
    };
  }
  if (keep === "height") {
    return {
      width: linkedSide(source, "height", target.height),
      height: target.height,
    };
  }
  return target;
}

/** The centred part of `source` with the same shape as `target`
 *  ("fill" mode: crop the edges instead of stretching). */
export function coverCrop(source: Size, target: Size): Rect {
  const scale = Math.max(
    target.width / source.width,
    target.height / source.height,
  );
  const width = Math.min(source.width, Math.round(target.width / scale));
  const height = Math.min(source.height, Math.round(target.height / scale));
  return {
    x: Math.floor((source.width - width) / 2),
    y: Math.floor((source.height - height) / 2),
    width,
    height,
  };
}

/** A plain-language problem with a requested size, or null if it's fine. */
export function checkSize({ width, height }: Size): string | null {
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return "Enter a width and a height.";
  }
  if (width < 1 || height < 1) return "Width and height must be at least 1 px.";
  if (width > MAX_SIDE || height > MAX_SIDE) {
    return `Each side can be at most ${MAX_SIDE.toLocaleString("en")} px.`;
  }
  if (width * height > MAX_PIXELS) {
    return `That's ${Math.round((width * height) / 1e6)} megapixels. Phones can only handle up to 16 megapixels, so please choose a smaller size.`;
  }
  return null;
}
